import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { AccessRequest, UserProfile } from "@/types";

interface UsersData {
  accessRequests: AccessRequest[];
  profiles: UserProfile[];
}

export { type UserProfile };

export function useUsersAdmin() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isUploading, setIsUploading] = useState(false);

  const fetchUsers = async (): Promise<UsersData> => {
    // Fetch access requests
    const { data: accessRequests, error: requestsError } = await supabase
      .from('access_requests')
      .select('*')
      .order('created_at', { ascending: false });

    if (requestsError) {
      toast({
        title: "Error loading access requests",
        description: requestsError.message,
        variant: "destructive"
      });
      throw requestsError;
    }

    // Fetch user profiles
    const { data: profiles, error: profilesError } = await supabase
      .from('user_profiles')
      .select('*')
      .order('created_at', { ascending: false });
      
    if (profilesError) {
      toast({
        title: "Error loading user profiles",
        description: profilesError.message,
        variant: "destructive"
      });
      throw profilesError;
    }

    // Get list of emails that already have user profiles
    const existingUserEmails = new Set((profiles || []).map(profile => profile.email));

    // Filter out access requests that have been converted to user profiles
    // Only show pending requests or requests that don't have corresponding user profiles
    const filteredRequests = (accessRequests || []).filter(request => {
      // Show only pending requests that don't have corresponding user profiles
      return request.status === 'pending' && !existingUserEmails.has(request.email);
    });

    // Format access requests to match our AccessRequest type
    const formattedRequests = filteredRequests.map(request => ({
      id: request.id,
      firstName: request.first_name,
      lastName: request.last_name,
      email: request.email,
      phone: request.phone,
      companyName: request.company_name,
      licenseNumber: request.license_number,
      message: request.message,
      status: request.status,
      createdAt: request.created_at,
      // Keep original fields for backwards compatibility
      first_name: request.first_name,
      last_name: request.last_name,
      company_name: request.company_name,
      license_number: request.license_number,
      created_at: request.created_at
    }));

    // Format user profiles to match our UserProfile type
    const formattedProfiles = (profiles || []).map(profile => ({
      id: profile.id,
      firstName: profile.first_name,
      lastName: profile.last_name,
      email: profile.email,
      phone: profile.phone,
      companyName: profile.company_name,
      licenseNumber: profile.license_number,
      licenseExpiry: profile.license_expiry,
      licenseType: profile.license_type,
      message: profile.message,
      isApproved: profile.is_approved,
      role: profile.role || 'customer',
      createdAt: profile.created_at,
      // New fields
      abnAcn: profile.abn_acn,
      companyAddress: profile.company_address,
      stateTerritory: profile.state_territory,
      licenseDocumentUrl: profile.license_document_url,
      companyLogoUrl: profile.company_logo_url,
      websiteUrl: profile.website_url,
      companyDescription: profile.company_description,
      operationalStatus: profile.operational_status,
      registrationDate: profile.registration_date,
      termsAgreement: profile.terms_agreement,
      isBanned: profile.is_banned,
      // Keep original fields for backwards compatibility
      first_name: profile.first_name,
      last_name: profile.last_name,
      company_name: profile.company_name,
      license_number: profile.license_number,
      license_expiry: profile.license_expiry,
      license_type: profile.license_type,
      is_approved: profile.is_approved,
      created_at: profile.created_at,
      abn_acn: profile.abn_acn,
      company_address: profile.company_address,
      state_territory: profile.state_territory,
      license_document_url: profile.license_document_url,
      company_logo_url: profile.company_logo_url,
      website_url: profile.website_url,
      company_description: profile.company_description,
      operational_status: profile.operational_status,
      registration_date: profile.registration_date,
      terms_agreement: profile.terms_agreement,
      is_banned: profile.is_banned
    }));

    return {
      accessRequests: formattedRequests,
      profiles: formattedProfiles
    };
  };

  const handleChangeRole = async (userId: string, newRole: string) => {
    try {
      const { error } = await supabase
        .from('user_profiles')
        .update({ role: newRole })
        .eq('id', userId);

      if (error) throw error;

      toast({ title: "User role updated successfully" });
      queryClient.invalidateQueries({ queryKey: ["users"] });
    } catch (error: any) {
      console.error("Error updating role:", error);
      toast({ 
        title: "Failed to update user role", 
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const handleApproveUser = async (userId: string) => {
    try {
      const { error } = await supabase
        .from('user_profiles')
        .update({ is_approved: true })
        .eq('id', userId);

      if (error) throw error;
      
      toast({ title: "User access approved successfully" });
      queryClient.invalidateQueries({ queryKey: ["users"] });
    } catch (error: any) {
      console.error("Error approving user:", error);
      toast({ 
        title: "Failed to approve user access", 
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const handleReject = async (userId: string) => {
    try {
      const { error } = await supabase
        .from('user_profiles')
        .update({ is_approved: false })
        .eq('id', userId);

      if (error) throw error;
      
      toast({ title: "User access revoked successfully" });
      queryClient.invalidateQueries({ queryKey: ["users"] });
    } catch (error: any) {
      console.error("Error revoking access:", error);
      toast({ 
        title: "Failed to revoke user access", 
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const handleBanUser = async (userId: string) => {
    try {
      const { error } = await supabase
        .from('user_profiles')
        .update({ is_banned: true })
        .eq('id', userId);

      if (error) throw error;
      
      toast({ title: "User banned successfully" });
      queryClient.invalidateQueries({ queryKey: ["users"] });
    } catch (error: any) {
      console.error("Error banning user:", error);
      toast({ 
        title: "Failed to ban user", 
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const handleUnbanUser = async (userId: string) => {
    try {
      const { error } = await supabase
        .from('user_profiles')
        .update({ is_banned: false })
        .eq('id', userId);

      if (error) throw error;
      
      toast({ title: "User unbanned successfully" });
      queryClient.invalidateQueries({ queryKey: ["users"] });
    } catch (error: any) {
      console.error("Error unbanning user:", error);
      toast({ 
        title: "Failed to unban user", 
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const handleDeleteUser = async (userId: string) => {
    try {
      const { error } = await supabase
        .from('user_profiles')
        .delete()
        .eq('id', userId);

      if (error) throw error;
      
      toast({ title: "User deleted successfully" });
      queryClient.invalidateQueries({ queryKey: ["users"] });
    } catch (error: any) {
      console.error("Error deleting user:", error);
      toast({ 
        title: "Failed to delete user", 
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const handleUpdateUserProfile = async (userId: string, data: Partial<UserProfile>) => {
    try {
      // Convert camelCase to snake_case for database
      const dbData = {
        first_name: data.firstName || data.first_name,
        last_name: data.lastName || data.last_name,
        email: data.email,
        phone: data.phone,
        company_name: data.companyName || data.company_name,
        license_number: data.licenseNumber || data.license_number,
        license_expiry: data.licenseExpiry || data.license_expiry,
        license_type: data.licenseType || data.license_type,
        role: data.role,
        is_approved: data.isApproved || data.is_approved,
        abn_acn: data.abnAcn || data.abn_acn,
        company_address: data.companyAddress || data.company_address,
        state_territory: data.stateTerritory || data.state_territory,
        license_document_url: data.licenseDocumentUrl || data.license_document_url,
        company_logo_url: data.companyLogoUrl || data.company_logo_url,
        website_url: data.websiteUrl || data.website_url,
        company_description: data.companyDescription || data.company_description,
        operational_status: data.operationalStatus || data.operational_status,
        terms_agreement: data.termsAgreement || data.terms_agreement,
        is_banned: data.isBanned || data.is_banned
      };

      const { error } = await supabase
        .from('user_profiles')
        .update(dbData)
        .eq('id', userId);

      if (error) throw error;
      
      toast({ title: "User profile updated successfully" });
      queryClient.invalidateQueries({ queryKey: ["users"] });
      return true;
    } catch (error: any) {
      console.error("Error updating user profile:", error);
      toast({ 
        title: "Failed to update user profile", 
        description: error.message,
        variant: "destructive"
      });
      return false;
    }
  };

  const uploadFile = async (file: File, userId: string, fileType: 'license' | 'logo') => {
    if (!file) return null;
    
    try {
      setIsUploading(true);
      const fileExt = file.name.split('.').pop();
      const fileName = `${userId}/${fileType}_${Date.now()}.${fileExt}`;
      const filePath = `${fileName}`;
      
      const { error: uploadError, data } = await supabase.storage
        .from('user-documents')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: true
        });
        
      if (uploadError) throw uploadError;
      
      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('user-documents')
        .getPublicUrl(filePath);
        
      return publicUrl;
    } catch (error: any) {
      console.error(`Error uploading ${fileType} document:`, error);
      toast({
        title: `Failed to upload ${fileType} document`,
        description: error.message,
        variant: "destructive"
      });
      return null;
    } finally {
      setIsUploading(false);
    }
  };

  // New function to reject an access request
  const handleRejectAccessRequest = async (requestId: string) => {
    try {
      const { error } = await supabase
        .from('access_requests')
        .update({ status: 'rejected' })
        .eq('id', requestId);

      if (error) throw error;
      
      toast({ title: "Access request rejected" });
      queryClient.invalidateQueries({ queryKey: ["users"] });
      return true;
    } catch (error: any) {
      console.error("Error rejecting access request:", error);
      toast({ 
        title: "Failed to reject access request", 
        description: error.message,
        variant: "destructive"
      });
      return false;
    }
  };

  // Add new function to create admin users directly
  const handleCreateAdminUser = async (userData: {
    email: string;
    firstName: string;
    lastName: string;
    password: string;
    role: string;
    isApproved: boolean;
  }) => {
    try {
      // Create the user in Supabase Auth
      const { data: authData, error: authError } = await supabase.functions.invoke("create-user", {
        body: {
          email: userData.email,
          password: userData.password,
          firstName: userData.firstName,
          lastName: userData.lastName,
          role: userData.role,
          isApproved: userData.isApproved
        }
      });
      
      if (authError) {
        console.error("Error creating user:", authError);
        toast({ 
          title: "Failed to create user", 
          description: authError.message,
          variant: "destructive"
        });
        return false;
      }
      
      if (authData?.success) {
        toast({ 
          title: "User created successfully", 
          description: `${userData.firstName} ${userData.lastName} has been created with ${userData.role} role.`
        });
        queryClient.invalidateQueries({ queryKey: ["users"] });
        return true;
      } else if (authData?.error) {
        toast({ 
          title: "Error creating user", 
          description: authData.error,
          variant: "destructive"
        });
        return false;
      }
      
      return false;
    } catch (error: any) {
      console.error("Error creating admin user:", error);
      toast({ 
        title: "Failed to create user", 
        description: error.message,
        variant: "destructive"
      });
      return false;
    }
  };

  return {
    fetchUsers,
    handleChangeRole,
    handleApproveUser,
    handleReject,
    handleBanUser,
    handleUnbanUser,
    handleDeleteUser,
    handleUpdateUserProfile,
    uploadFile,
    isUploading,
    handleRejectAccessRequest,
    handleCreateAdminUser // Add the new function to the returned object
  };
}
